import React, {useCallback, useEffect, useMemo, useRef} from 'react';
import cn from 'classnames';

import ViewportMgr from 'coreClasses/view/ViewportMgr';
import {useSetUI as useUIMgr, useUI} from 'coreHooks';

import Carousel, {ConfigType, ECarouselDirection, ECarouselPaginationType} from 'brandComponents/Carousel';
import Icon from 'brandComponents/Icon';
import {ZoomablePicture} from 'brandComponents/Picture';
import {PRODUCT_IMAGES_SETTINGS, ViewType} from 'brandConstants';

import {IProductDetailZoomCarouselProps} from './IProductDetailZoomCarousel';

import styles from './ProductDetailZoomCarousel.module.scss';

const ProductDetailZoomCarousel = ({product}: IProductDetailZoomCarouselProps) => {
	const UIMgr = useUIMgr();
	const isServerUI = useUI('isServer');
	const {imageIndex, isActive: isZoomActive} = useUI('pdpZoom');
	const swiperRef = useRef(null);

	const carouselConfig: ConfigType = useMemo(() => {
		const formatPaginationNumber = (number) => number < 10 ? `0${number}` : number;

		return {
			loop: true,
			direction: ECarouselDirection.HORIZONTAL,
			slidesPerView: 1,
			initialSlide: 0,
			navigationConfig: {
				enabled: true,
				prevBtnIcon: 'arrow-left',
				nextBtnIcon: 'arrow-right',
			},
			paginationConfig:{
				enabled: true,
				type: ECarouselPaginationType.FRACTION,
				renderBullet: function(_, className) {
					return `<span class=" ${className} "></span>`;
				},
				formatFractionCurrent: (number) => formatPaginationNumber(number),
				formatFractionTotal: (number) => formatPaginationNumber(number)
			},
			mousewheel: false,
			duplicateSlides: {
				enabled: false,
				slidesNumber: 0,
			},
			allowTouchMove: false
		} as ConfigType;
	}, []);

	const slides = useMemo(() => (
		(product.getProductImages(ViewType.ZOOM, PRODUCT_IMAGES_SETTINGS.productZoom) || [])
			.map((image, index) => (
				{
					key: image.getUrl(),
					elem: (
						<ZoomablePicture
							image={image}
							index={index}
							isPinchToZoomEnabled={!isServerUI && ViewportMgr.query('<x-large')}
							zoomOnClickEnabled={false}
							isChangeSlideAtSwipeEnabled={false}
							recenterOnChange={true}
							showPlaceholder={PRODUCT_IMAGES_SETTINGS.ShowPlaceholder.pdp}
							showSkeleton={!isServerUI}
							onClick={(e) => {
								UIMgr.setUI({
									pdpZoom: {
										isActive: false,
										clickedCoordinates: {
											x: e.nativeEvent.clientX,
											y: e.nativeEvent.clientY - e.target.getBoundingClientRect().top,
										}
									}
								});
							}}
						/>
					),
				}
			))
	), [product.getProductId(), isZoomActive]);

	const centerSlides = useCallback((swiper) => {
		if (ViewportMgr.query('>=large')) {
			swiper?.slides?.forEach((slide) => {
				const middleY = (slide.scrollHeight - slide.clientHeight) / 2;
				const middleX = (slide.scrollWidth - slide.clientWidth) / 2;

				if (slide.scrollTop !== middleY && slide.scrollHeight) {
					slide.scrollTo({
						top: middleY,
						left: middleX,
						behavior: 'instant',
					});
				}
			});
		}
	}, []);

	const onChangeHandler = useCallback((swiper) => {
		UIMgr.setUI({
			pdpZoom: {
				imageIndex: swiper.realIndex,
			},
		});

		centerSlides(swiper);
	}, []);

	const onInitHandler = useCallback((swiper) => {
		swiperRef.current = swiper;
	}, []);

	useEffect(() => {
		if (swiperRef.current && isZoomActive) {
			swiperRef.current.slideToLoop(imageIndex, 0);
		}

		centerSlides(swiperRef?.current);
	}, [isZoomActive]);

	return (
		<div className={cn(
			styles['product-detail-zoom-carousel'],
			{[styles['active']]: isZoomActive}
		)}>
			<Carousel
				config={carouselConfig}
				handlers={{onChangeHandler, onInitHandler}}
				key={`imagesCarousel_${product.getProductId()}_zoom'`}
			>
				{slides}
			</Carousel>
			<span
				className={cn(styles['product-detail-zoom-carousel__close'])}
				onClick={() => {
					UIMgr.setUI({
						pdpZoom: {
							isActive: false,
						},
					});
				}}
			>
				<Icon name="close" width={24} height={24} />
			</span>
		</div>
	);
};

export default ProductDetailZoomCarousel;
